import { Calendar, DollarSign, Layers, Package, Store, Target } from "lucide-react";
import { useState } from "react";
import useCurrentPlan from "../../../hooks/useCurrentPlan";
import MetaCard from "./atoms/metaCard";

function calcWeeks(from: string | undefined, to: string | undefined): number | null {
    if (!from || !to) return null;
    const a = new Date(from);
    const b = new Date(to);
    if (isNaN(a.getTime()) || isNaN(b.getTime()) || b <= a) return null;
    return Math.round((b.getTime() - a.getTime()) / (7 * 24 * 60 * 60 * 1000));
}

const ScenarioDefinition: React.FC = () => {
    const { headingData, currentPlan, currentPlanData } = useCurrentPlan();
    const [planStartDateOverride, setPlanStartDateOverride] = useState<string | null>(null);
    const [planEndDateOverride, setPlanEndDateOverride] = useState<string | null>(null);

    const planData = currentPlan?.plan_by_retailer[0];
    const planStartDate = planStartDateOverride
        ?? planData?.retailer_plan_start_date
        ?? "";

    const planEndDate = planEndDateOverride
        ?? planData?.retailer_plan_end_date
        ?? "";

    const weeks = calcWeeks(planStartDate, planEndDate);

    return (
        <div className="d-flex flex-column gap-2">
            <div>
                <p className="small text-uppercase fw-semibold !k-text-subtle mb-1 letter-spacing-wide" style={{ fontSize: "11px" }}>
                    Scenario Definition
                </p>
                <p className="small fw-normal !k-text-subtle mb-0" style={{ fontSize: "12px" }}>
                    Simulation configuration used to generate this plan
                </p>
            </div>

            {/* Row 1 — Retailer / Planning Level / Timeframe */}
            <div className="row g-2">
                <div className="col-md-4 d-flex">
                    <MetaCard
                        icon={<Store size={14} />}
                        label="Retailer"
                        value={headingData?.retailers ?? ""}
                    />
                </div>
                <div className="col-md-4 d-flex">
                    <MetaCard
                        icon={<Layers size={14} />}
                        label="Planning Level"
                        value={currentPlanData?.planningLevel ?? ""}
                    />
                </div>

                {/* Timeframe card — interactive date range */}
                <div className="col-md-4 d-flex">
                    <div className="scenario-meta-card rounded-3 px-3 d-flex flex-column h-100 w-100">
                        <div className="d-flex align-items-center gap-1 text-secondary mb-1">
                            <Calendar className="flex-shrink-0" size={14} />
                            <span className="scenario-meta-label text-uppercase fw-semibold">Timeframe</span>
                            {weeks && (
                                <span className="badge rounded-pill ms-auto px-2 py-1 fw-bold scenario-weeks-badge">
                                    {weeks}
                                    w
                                </span>
                            )}
                        </div>
                        <div className="d-flex align-items-center gap-1">
                            <input
                                type="date"
                                onChange={event => setPlanStartDateOverride(event.target.value)}
                                value={planStartDate}
                                className="form-control form-control-sm border-0 bg-transparent p-0 small fw-semibold shadow-none"
                                style={{ colorScheme: "light" }}
                            />

                            <span className="text-secondary small flex-shrink-0">→</span>

                            <input
                                type="date"
                                onChange={event => setPlanEndDateOverride(event.target.value)}
                                value={planEndDate}
                                className="form-control form-control-sm border-0 bg-transparent p-0 small fw-semibold shadow-none"
                                style={{ colorScheme: "light" }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Row 2 — Selection / Goal / Budget */}
            <div className="row g-2">
                <div className="col-md-6 d-flex">
                    <MetaCard
                        icon={<Package size={14} />}
                        label="Selection"
                        value={headingData?.skuName ?? headingData?.subBrandName ?? ""}
                    />
                </div>
                <div className="col-md-3 d-flex">
                    <MetaCard
                        icon={<Target size={14} />}
                        label="Goal"
                        value={headingData?.objective ?? ""}
                    />
                </div>
                <div className="col-md-3 d-flex">
                    <MetaCard
                        icon={<DollarSign size={14} />}
                        label="Budget"
                        value={currentPlanData?.budget ?? ""}
                    />
                </div>
            </div>
        </div>
    );
};

export default ScenarioDefinition;
